import LocationOverviewSchema from '@atoms/schemas/locationOverviewSchema';
import useLocale from '@hooks/useLocale';
import { useOverview } from '@hooks/useOverview';
import { getLocationPages } from '@lib/prepr/location';
import Filters from '@molecules/Filters/Filters';
import CardGrid from '@organisms/CardGrid/CardGrid';
import Hero from '@organisms/Hero/Hero';
import Sections from '@organisms/Sections/Sections';
import { FilterGroup } from '@type-declarations/filters';
import { LocationOverviewPage } from '@type-declarations/page';
import { groupCategoryFilters } from '@utils/filters/categoryFilter';
import { tagToFilter } from '@utils/filters/tagFilter';
import { useRef } from 'react';

interface Props {
  page: LocationOverviewPage;
}

const translations: {
  nl: { [key: string]: string };
  en: { [key: string]: string };
} = {
  nl: {
    searchLabel: 'Zoek naar locatie',
    tags: 'Kenmerken',
    categories: 'Categorieën',
    nothingFoundTitle: 'Er zijn geen locaties gevonden',
    nothingFoundText: 'Pas uw filter input aan of probeer het later nog eens.',
  },
  en: {
    searchLabel: 'Search for location',
    tags: 'Tags',
    categories: 'Categories',
    nothingFoundTitle: 'No locations found',
    nothingFoundText: 'Try changing your filter input or try again later.',
  },
};

function LocationOverviewTemplate({ page }: Props) {
  // TODO: This component gets rerendered 5 times when a tag or search input is clicked. Fix this!!!
  const locale = useLocale();

  const scrollToRef = useRef<HTMLDivElement | null>(null);

  const {
    title,
    introText,
    image,
    items: initItems,
    total: initTotal,
    alignment,
    highlightedTag,
  } = page;

  const {
    total,
    items,
    activeFilters,
    clearFilters,
    handleChange,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    handleNextPage,
    handlePrevPage,
    handleGoToPage,
    loading,
  } = useOverview({
    initItems,
    initTotal,
    scrollToRef,
    fetch: getLocationPages,
  });

  const categoryGroups: FilterGroup[] = groupCategoryFilters(page.categories);

  const primaryTags = tagToFilter(page.primaryTags);

  return (
    <>
      <Hero
        title={title}
        text={introText}
        image={image}
        colorVariant="gray"
        alignment={alignment}
      />

      <Filters
        highlightedTag={highlightedTag}
        primary={[
          {
            key: 'tags',
            filters: primaryTags,
          },
        ]}
        secondary={[
          ...categoryGroups,
          {
            title: translations[locale].tags,
            key: 'tags',
            filters: [...primaryTags, ...tagToFilter(page.extraTags)],
          },
        ]}
        activeFilters={activeFilters}
        searchLabel={translations[locale].searchLabel}
        onChange={handleChange}
        onClear={clearFilters}
      />

      <CardGrid
        total={total}
        loading={loading}
        notFoundTitle={translations[locale].nothingFoundTitle}
        notFoundText={translations[locale].nothingFoundText}
        items={items}
        scrollToRef={scrollToRef}
        currentPage={currentPage}
        totalPages={totalPages}
        hasPrevPage={hasPrevPage}
        hasNextPage={hasNextPage}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
        handleGoToPage={handleGoToPage}
      />

      <Sections sections={page.sections} />

      <LocationOverviewSchema items={items} page={page} />
    </>
  );
}

export default LocationOverviewTemplate;
